import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import styles from "./Modal.module.css";
import { Icon } from "@iconify/react";
import axios from "axios";
import { useSelector } from "react-redux";

const DepositWithdrawModal = memo(({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [qrData, setQrData] = useState(null);
  const currentUser = useSelector((s) => s?.user?.currentUser);

  const qrImageSrc = useMemo(() => {
    const d = qrData?.data;
    if (!d) return "";
    if (d.qr_code_url) return d.qr_code_url;
    if (d.qr_code) return `data:image/png;base64,${d.qr_code}`;
    return "";
  }, [qrData]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const handleEscapeKey = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, handleEscapeKey]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab("deposit");
    setAmount("");
    setError("");
    setQrData(null);
  }, [isOpen]);

  const handleCreateQr = useCallback(async () => {
    try {
      setError("");
      setQrData(null);
      const uid = currentUser?.id;
      if (!uid) {
        setError("กรุณาเข้าสู่ระบบก่อน");
        return;
      }
      const a = String(amount || "").trim();
      if (!a) {
        setError("กรุณากรอกจำนวนเงิน");
        return;
      }
      const n = Number(a);
      if (!Number.isFinite(n) || n <= 0) {
        setError("จำนวนเงินไม่ถูกต้อง");
        return;
      }

      setLoading(true);
      const res = await axios.post("/onpay/deposit/create-qr-code", {
        amount: a,
        userId: String(uid),
      });
      setQrData(res.data);
      if (!res.data?.success) {
        setError(res.data?.message || "สร้าง QR Code ไม่สำเร็จ");
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error?.message ||
        e?.message ||
        "สร้าง QR Code ไม่สำเร็จ";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [amount, currentUser?.id]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>💰 ฝาก-ถอนเงิน</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${
                activeTab === "deposit" ? styles.active : ""
              }`}
              onClick={() => setActiveTab("deposit")}
            >
              ฝากเงิน
            </button>
            <button
              className={`${styles.tab} ${
                activeTab === "withdraw" ? styles.active : ""
              }`}
              onClick={() => setActiveTab("withdraw")}
            >
              ถอนเงิน
            </button>
          </div>

          {activeTab === "deposit" ? (
            <div className={styles.form}>
              <div
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 12,
                  padding: "14px 16px",
                  marginBottom: 4,
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#fff",
                    marginBottom: 4,
                  }}
                >
                  ชำระผ่าน ONPAY
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
                  สแกน QR Code เพื่อฝากเงินเข้าสู่ระบบอัตโนมัติ
                </div>
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.label}>จำนวนเงิน (บาท)</label>
                <input
                  type="number"
                  className={styles.input}
                  placeholder="ระบุจำนวนเงินที่ต้องการฝาก"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={loading}
                  style={{ padding: "14px 16px" }}
                />
              </div>

              {error ? <div className={styles.errorText}>{error}</div> : null}

              <button
                className={styles.submitButton}
                onClick={handleCreateQr}
                disabled={loading}
              >
                {loading ? "⏳ กำลังสร้าง..." : "สร้าง QR Code"}
              </button>

              {qrImageSrc ? (
                <div
                  style={{
                    marginTop: 16,
                    textAlign: "center",
                    background: "#fff",
                    borderRadius: 14,
                    padding: 16,
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <img
                    src={qrImageSrc}
                    alt="QR Code"
                    style={{ maxWidth: "100%", borderRadius: 8 }}
                  />
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      color: "#666",
                      fontWeight: 600,
                    }}
                  >
                    สแกน QR เพื่อชำระเงิน
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div className={styles.form}>
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "rgba(255,255,255,0.35)",
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                <Icon
                  icon="solar:wallet-bold-duotone"
                  style={{
                    fontSize: 48,
                    marginBottom: 12,
                    display: "block",
                    margin: "0 auto 12px",
                  }}
                />
                ระบบถอนเงินยังไม่เปิดให้บริการในขณะนี้
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

DepositWithdrawModal.displayName = "DepositWithdrawModal";

export default DepositWithdrawModal;
